import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { navItems } from '@/mocks/homeData';
import { useAuth } from '@/hooks/useAuth';
import supabase from '@/lib/supabase';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.user_metadata?.is_admin === true || user?.app_metadata?.is_admin === true;
  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('스카연');
  const [logoLoading, setLogoLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('site_settings')
      .select('key, value')
      .in('key', ['site_logo', 'site_name'])
      .then(({ data }) => {
        if (data) {
          const logoEntry = data.find((d) => d.key === 'site_logo');
          const nameEntry = data.find((d) => d.key === 'site_name');
          if (logoEntry?.value) setSiteLogo(logoEntry.value);
          if (nameEntry?.value) setSiteName(nameEntry.value);
        }
      })
      .catch(() => {})
      .finally(() => setLogoLoading(false));
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="bg-secondary-950 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 min-w-[140px]">
              {logoLoading ? (
                <div className="w-8 h-8 bg-secondary-800 rounded animate-pulse" />
              ) : siteLogo ? (
                <img src={siteLogo} alt={siteName} className="h-8 md:h-10 object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-primary-500 rounded flex items-center justify-center">
                    <span className="text-white font-bold text-sm">스</span>
                  </div>
                  <span className="text-xl font-bold text-primary-500">{siteName}</span>
                </>
              )}
            </Link>
          </div>

          <div className="hidden md:flex items-center flex-1 max-w-lg mx-8">
            <div className="flex w-full">
              <input
                type="text"
                placeholder="사이트명 또는 도메인을 입력하세요."
                className="flex-1 bg-secondary-900 text-white text-sm px-4 py-2 rounded-l border-2 border-secondary-500 focus:outline-none focus:border-primary-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigate(`/search?q=${encodeURIComponent((e.target as HTMLInputElement).value)}`);
                  }
                }}
              />
              <button
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-r transition-colors"
                onClick={() => {
                  const input = document.querySelector<HTMLInputElement>('input[placeholder="사이트명 또는 도메인을 입력하세요."]');
                  if (input) {
                    navigate(`/search?q=${encodeURIComponent(input.value)}`);
                  } else {
                    navigate('/search');
                  }
                }}
              >
                <i className="ri-search-line" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative">
                <button
                  className="hidden md:flex items-center gap-2 text-sm text-white hover:text-primary-500 transition-colors whitespace-nowrap"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {(user.user_metadata?.nickname || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span>{user.user_metadata?.nickname || user.email}</span>
                  {userMenuOpen ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border border-foreground-200 z-50">
                    <div className="px-4 py-3 border-b border-foreground-200">
                      <p className="text-sm font-medium text-foreground-950">
                        {user.user_metadata?.nickname || '사용자'}
                      </p>
                      <p className="text-xs text-foreground-600 truncate">{user.email}</p>
                    </div>
                    <div className="py-1">
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="w-full text-left px-4 py-2 text-sm text-primary-600 hover:bg-background-100 transition-colors flex items-center gap-2"
                        >
                          <i className="ri-shield-keyhole-line text-sm" />
                          관리자 대시보드
                        </Link>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                      >
                        로그아웃
                      </button>
                    </div>
                  </div>
                )}

                <Link
                  to="/"
                  className="md:hidden w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center"
                >
                  <span className="text-white text-xs font-bold">
                    {(user.user_metadata?.nickname || 'U').charAt(0).toUpperCase()}
                  </span>
                </Link>
              </div>
            ) : (
              <>
                <Link to="/login" className="hidden md:block text-sm text-white hover:text-primary-500 transition-colors whitespace-nowrap">
                  로그인
                </Link>
                <Link to="/register" className="hidden md:block bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded transition-colors whitespace-nowrap">
                  회원가입
                </Link>
              </>
            )}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="메뉴 열기"
            >
              <i className="ri-menu-line text-xl" />
            </button>
            {user ? (
              <Link to="/report" className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded transition-colors whitespace-nowrap hidden sm:block">
                피해 제보하기
              </Link>
            ) : (
              <Link to={`/login?redirect=${encodeURIComponent('/report')}`} className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded transition-colors whitespace-nowrap hidden sm:block">
                피해 제보하기
              </Link>
            )}
          </div>
        </div>
      </div>

      <nav className="hidden md:block border-t border-secondary-800">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center h-12 gap-8">
            {navItems.map((item) => (
              item.href !== '#' ? (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    item.active ? 'text-primary-500' : 'text-foreground-200 hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  key={item.label}
                  className="text-sm font-medium text-foreground-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                >
                  {item.label}
                </span>
              )
            ))}
            <button className="ml-auto text-sm text-foreground-200 hover:text-white flex items-center gap-1 whitespace-nowrap">
              <i className="ri-menu-line" />
              전체메뉴
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-secondary-800 bg-secondary-900">
          <div className="px-4 py-3 space-y-2">
            <div className="flex mb-3">
              <input
                type="text"
                placeholder="사이트명 또는 도메인을 입력하세요."
                className="flex-1 bg-secondary-800 text-white text-sm px-3 py-2 rounded-l border-2 border-secondary-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setMobileOpen(false);
                    navigate(`/search?q=${encodeURIComponent((e.target as HTMLInputElement).value)}`);
                  }
                }}
              />
              <button
                className="bg-primary-500 text-white px-3 py-2 rounded-r"
                onClick={() => {
                  const input = document.querySelector<HTMLInputElement>('.md\\:hidden input[type="text"]');
                  if (input) {
                    setMobileOpen(false);
                    navigate(`/search?q=${encodeURIComponent(input.value)}`);
                  }
                }}
              >
                <i className="ri-search-line" />
              </button>
            </div>
            {navItems.map((item) => (
              item.href !== '#' ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`block py-2 text-sm font-medium ${
                    item.active ? 'text-primary-500' : 'text-foreground-200'
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  key={item.label}
                  className="block py-2 text-sm font-medium text-foreground-200 cursor-pointer"
                >
                  {item.label}
                </span>
              )
            ))}
            <div className="flex gap-2 pt-2 border-t border-secondary-800">
              {user ? (
                <>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 text-center text-sm text-white py-2 bg-primary-500 rounded whitespace-nowrap"
                    >
                      관리자
                    </Link>
                  )}
                  <Link
                    to="/report"
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center text-sm text-white py-2 bg-primary-500 rounded whitespace-nowrap"
                  >
                    피해 제보하기
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex-1 text-center text-sm text-white py-2 border border-secondary-700 rounded whitespace-nowrap"
                  >
                    로그아웃
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={`/login?redirect=${encodeURIComponent('/report')}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center text-sm text-white py-2 bg-primary-500 rounded whitespace-nowrap"
                  >
                    피해 제보하기
                  </Link>
                  <Link to="/login" className="flex-1 text-center text-sm text-white py-2 border border-secondary-700 rounded whitespace-nowrap">
                    로그인
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}